import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { imageSrc } from "./api";

const C = (h) => new THREE.Color(h);
const rand = (a, b) => a + Math.random() * (b - a);
const smooth = (t) => t * t * (3 - 2 * t);
const WALL_Z = -4.6;
const PAINT_Y = 1.65;

const waterVert = `varying vec3 vW; void main(){ vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`;
const waterFrag = `
uniform float uTime, uCalm, uMirror; uniform vec4 uRip[10]; uniform vec3 uDeep, uWater, uSky, uHorizon, uSun, uCam;
varying vec3 vW;
float waves(vec2 p, float t){ return sin(p.x*1.3+t*0.9)*0.5 + sin(p.y*1.7-t*1.1)*0.4 + sin((p.x+p.y)*2.9+t*1.6)*0.22 + sin((p.x-p.y*0.6)*5.3-t*2.1)*0.12; }
float rip(vec2 p, float t){
  float h = 0.0;
  for (int i = 0; i < 10; i++) {
    vec4 r = uRip[i]; float age = t - r.z;
    if (age < 0.0 || age > 7.0) continue;
    float d = distance(p, r.xy); float k = d - age * 1.3;
    h += sin(k * 10.0) * exp(-k * k * 2.5) * exp(-age * 0.45) * r.w / (1.0 + d * 0.5);
  }
  return h;
}
float H(vec2 p, float t){ return waves(p, t) * 0.03 * mix(1.0, 0.03, uCalm) + rip(p, t) * 0.07 * (1.0 - uCalm * 0.95); }
void main(){
  vec2 p = vW.xz; float t = uTime; float e = 0.04;
  float h0 = H(p, t);
  vec3 n = normalize(vec3(-(H(p + vec2(e, 0.0), t) - h0) / e, 1.0, -(H(p + vec2(0.0, e), t) - h0) / e));
  vec3 v = normalize(uCam - vW);
  float fres = pow(1.0 - max(dot(n, v), 0.0), 3.0);
  vec3 r = reflect(-v, n);
  vec3 sky = mix(uHorizon, uSky, smoothstep(0.0, 0.45, r.y));
  vec3 col = mix(uDeep, uWater, clamp(0.55 + n.x * 5.0 + n.z * 3.0, 0.0, 1.0));
  col = mix(col, sky, 0.1 + fres * 0.42);
  float spec = pow(max(dot(r, normalize(uSun)), 0.0), 420.0);
  col += vec3(1.0, 0.94, 0.84) * spec * 1.3 * (1.0 - uCalm * 0.6);
  float dist = length(p - uCam.xz);
  col = mix(col, uHorizon * 0.85, smoothstep(14.0, 44.0, dist) * 0.8);
  float a = mix(0.93, 0.5, uMirror) + fres * 0.35;
  gl_FragColor = vec4(col, clamp(a, 0.0, 1.0));
  #include <colorspace_fragment>
}`;
const skyFrag = `
uniform vec3 uSky, uHorizon, uSun; varying vec3 vW;
void main(){ vec3 d = normalize(vW); vec3 c = mix(uHorizon, uSky, smoothstep(-0.05, 0.5, d.y));
  c += vec3(1.0, 0.9, 0.78) * pow(max(dot(d, normalize(uSun)), 0.0), 24.0) * 0.35;
  gl_FragColor = vec4(c, 1.0);
  #include <colorspace_fragment>
}`;
const treeFrag = `
uniform vec3 uTree, uHorizon; varying vec2 vUv;
float hash(float n){ return fract(sin(n * 91.7) * 43758.5); }
float row(float x, float y, float sc, float base){
  float cx = x * sc; float id = floor(cx); float f = abs(fract(cx) - 0.5);
  float hh = base + 0.16 * hash(id + sc);
  float w = 0.42 * (1.0 - y / hh);
  return (y < 0.03 || (y < hh && f < w)) ? 1.0 : 0.0;
}
void main(){
  float x = vUv.x * 120.0; float y = vUv.y;
  float front = row(x, y, 1.3, 0.2);
  float back = row(x + 0.37, y, 2.1, 0.3);
  if (front + back < 0.5) discard;
  vec3 c = front > 0.5 ? mix(uHorizon * 0.7, uTree, 0.6) : mix(uHorizon * 0.85, uTree, 0.3);
  gl_FragColor = vec4(c, 1.0);
  #include <colorspace_fragment>
}`;
const mistFrag = `
uniform float uTime, uMist, uSeed; uniform vec3 uColor; varying vec2 vUv;
float n2(vec2 p){ return sin(p.x * 1.7 + sin(p.y * 1.3 + uSeed)) * 0.5 + sin(p.y * 2.3 - p.x * 0.7 + uSeed * 2.0) * 0.5; }
void main(){
  vec2 p = vUv * vec2(6.0, 2.0) + vec2(uTime * 0.05, 0.0);
  float a = (0.5 + 0.5 * n2(p)) * (0.6 + 0.4 * n2(p * 2.1 + 3.0));
  a *= smoothstep(0.0, 0.25, vUv.y) * smoothstep(1.0, 0.6, vUv.y) * smoothstep(0.0, 0.15, vUv.x) * smoothstep(1.0, 0.85, vUv.x);
  gl_FragColor = vec4(uColor, a * 0.5 * uMist);
  #include <colorspace_fragment>
}`;
const wallFrag = `
uniform float uFade, uMirrored; uniform vec3 uPlaster; varying vec2 vUv;
float h(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
void main(){
  vec2 p = vUv * vec2(9.0, 9.0);
  float g = h(floor(p * 40.0)) * 0.04 + h(floor(p * 9.0)) * 0.03;
  float spot = exp(-pow(distance(p, vec2(4.5, ${PAINT_Y.toFixed(2)})) / 1.9, 2.0));
  vec3 c = uPlaster * (0.62 + g + spot * 0.5);
  float a = uFade;
  if (uMirrored > 0.5) a *= pow(1.0 - vUv.y, 1.6) * 0.8 * smoothstep(0.0, 0.06, vUv.x) * smoothstep(1.0, 0.94, vUv.x);
  else a = smoothstep(0.0, 0.85, uFade);
  gl_FragColor = vec4(c, a);
  #include <colorspace_fragment>
}`;
const uvVert = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
const worldVert = `varying vec3 vW; void main(){ vW = (modelMatrix * vec4(position, 1.0)).xyz - cameraPosition; gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0); }`;

function petalGeo(len, wid, base, tip) {
  const g = new THREE.PlaneGeometry(wid, len, 2, 6);
  g.translate(0, len / 2, 0);
  const pos = g.attributes.position;
  const col = [];
  const a = C(base);
  const b = C(tip);
  for (let i = 0; i < pos.count; i++) {
    const v = pos.getY(i) / len;
    pos.setX(i, pos.getX(i) * Math.pow(Math.sin(Math.PI * Math.min(0.98, v * 0.92 + 0.06)), 0.7));
    pos.setZ(i, -v * v * len * 0.45 + Math.abs(pos.getX(i)) * 0.35);
    const c = a.clone().lerp(b, v);
    col.push(c.r, c.g, c.b);
  }
  g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  g.computeVertexNormals();
  return g;
}

function flowerGeo(rings, base, tip, centre) {
  const parts = [];
  rings.forEach(([n, tilt, len, wid, off]) => {
    for (let i = 0; i < n; i++) {
      const p = petalGeo(len, wid, base, tip);
      p.rotateX(-tilt);
      p.rotateY((i / n) * Math.PI * 2 + off);
      parts.push(p);
    }
  });
  const pod = new THREE.CylinderGeometry(0.035, 0.028, 0.04, 8).toNonIndexed();
  pod.translate(0, 0.03, 0);
  const cc = C(centre);
  pod.setAttribute("color", new THREE.Float32BufferAttribute(Array.from({ length: pod.attributes.position.count }, () => [cc.r, cc.g, cc.b]).flat(), 3));
  return mergeGeometries([...parts.map((p) => p.toNonIndexed()), pod]);
}

function padGeo() {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.absarc(0, 0, 1, 0.18, Math.PI * 2 - 0.18, false);
  s.lineTo(0, 0);
  const g = new THREE.ShapeGeometry(s, 20);
  g.rotateX(-Math.PI / 2);
  return g;
}

const clearZone = (x, z) => Math.abs(x) < 2.4 && z < -0.4 && z > WALL_Z - 0.5;
function scatter(n, r0, r1) {
  const out = [];
  while (out.length < n) {
    const a = Math.random() * Math.PI * 2;
    const r = r0 + Math.sqrt(Math.random()) * (r1 - r0);
    const x = Math.sin(a) * r;
    const z = Math.cos(a) * r;
    if (!clearZone(x, z)) out.push({ x, z, bx: x, bz: z, vx: 0, vz: 0, bob: 0, ph: rand(0, 6), rot: rand(0, 6), s: 1 });
  }
  return out;
}

export function createLotusScene(canvas, painting, { onLowFps } = {}) {
  const pal = painting.palette || [];
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: window.devicePixelRatio < 2 });
  renderer.setPixelRatio(Math.min(1.25, window.devicePixelRatio || 1));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(62, 1, 0.05, 200);
  const sky = C("#DCD4E4");
  const horizon = C("#F2DCCF");
  const sun = new THREE.Vector3(-0.5, 0.28, -0.8);
  const deep = C(pal[0] || "#424756").lerp(C("#1F2738"), 0.35);
  const water = C(pal[1] || "#4f4b59").lerp(C("#5E7487"), 0.3);
  const mistCol = C(pal[2] || "#9e979b").lerp(C("#F4EEF2"), 0.7);
  scene.add(new THREE.HemisphereLight("#FFF1E6", "#3A4A55", 1.4));
  const dl = new THREE.DirectionalLight("#FFE2C8", 1.3);
  dl.position.copy(sun).multiplyScalar(10);
  scene.add(dl);

  const skyMesh = new THREE.Mesh(new THREE.SphereGeometry(120, 24, 16), new THREE.ShaderMaterial({ vertexShader: worldVert, fragmentShader: skyFrag, side: THREE.BackSide, depthWrite: false, uniforms: { uSky: { value: sky }, uHorizon: { value: horizon }, uSun: { value: sun } } }));
  scene.add(skyMesh);
  const trees = new THREE.Mesh(new THREE.CylinderGeometry(40, 40, 18, 64, 1, true), new THREE.ShaderMaterial({ vertexShader: uvVert, fragmentShader: treeFrag, side: THREE.BackSide, uniforms: { uTree: { value: C(pal[0] || "#424756").lerp(C("#2A3346"), 0.4) }, uHorizon: { value: horizon } } }));
  trees.position.y = 8.5;
  scene.add(trees);
  const bed = new THREE.Mesh(new THREE.PlaneGeometry(200, 200), new THREE.MeshBasicMaterial({ color: deep.clone().multiplyScalar(0.7) }));
  bed.rotation.x = -Math.PI / 2;
  bed.position.y = -5.5;
  scene.add(bed);

  const rip = Array.from({ length: 10 }, () => new THREE.Vector4(0, 0, -99, 0));
  const wu = { uTime: { value: 0 }, uCalm: { value: 0 }, uMirror: { value: 0 }, uRip: { value: rip }, uDeep: { value: deep }, uWater: { value: water }, uSky: { value: sky }, uHorizon: { value: horizon }, uSun: { value: sun }, uCam: { value: new THREE.Vector3() } };
  const waterMesh = new THREE.Mesh(new THREE.PlaneGeometry(200, 200, 1, 1), new THREE.ShaderMaterial({ vertexShader: waterVert, fragmentShader: waterFrag, uniforms: wu, transparent: true, depthWrite: false }));
  waterMesh.rotation.x = -Math.PI / 2;
  waterMesh.renderOrder = 2;
  scene.add(waterMesh);

  const mists = [0, 1, 2, 3, 4].map((i) => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(34, 3.2), new THREE.ShaderMaterial({ vertexShader: uvVert, fragmentShader: mistFrag, transparent: true, depthWrite: false, side: THREE.DoubleSide, uniforms: { uTime: wu.uTime, uMist: { value: 1 }, uSeed: { value: i * 1.9 }, uColor: { value: mistCol } } }));
    const a = (i / 5) * Math.PI * 2 + 0.4;
    m.position.set(Math.sin(a) * 9, 0.7, Math.cos(a) * 9);
    m.lookAt(0, 0.7, 0);
    m.renderOrder = 3;
    scene.add(m);
    return m;
  });

  const lam = new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide });
  const kinds = [
    { geo: flowerGeo([[6, 0.22, 0.2, 0.09, 0], [8, 0.55, 0.26, 0.11, 0.3], [10, 0.95, 0.27, 0.12, 0.1]], "#D9789F", "#FBE1EC", "#F2C14E"), n: 26, y: 0.03 },
    { geo: flowerGeo([[6, 0.25, 0.19, 0.09, 0], [8, 0.6, 0.25, 0.11, 0.3], [10, 1.0, 0.26, 0.12, 0.1]], "#E9D8E4", "#FFFFFF", "#F2C14E"), n: 14, y: 0.03 },
    { geo: flowerGeo([[12, 1.15, 0.13, 0.06, 0], [12, 1.42, 0.15, 0.065, 0.26]], "#F6EFD8", "#FFFFFF", "#F4CB45"), n: 30, y: 0.02 },
  ];
  const pads = scatter(170, 1.1, 28);
  const padMesh = new THREE.InstancedMesh(padGeo(), new THREE.MeshLambertMaterial({ side: THREE.DoubleSide }), pads.length);
  pads.forEach((p, i) => {
    p.s = rand(0.18, 0.55);
    padMesh.setColorAt(i, C(i % 3 ? "#3F5E45" : "#5E7B52").lerp(C("#2F4A3F"), Math.random() * 0.4));
  });
  scene.add(padMesh);
  const flowers = kinds.map((k) => {
    const list = scatter(k.n, 1.4, 22);
    list.forEach((p) => (p.s = rand(0.8, 1.4)));
    const mesh = new THREE.InstancedMesh(k.geo, lam, list.length);
    scene.add(mesh);
    return { mesh, list, y: k.y };
  });
  const all = [{ mesh: padMesh, list: pads, y: 0.012, pad: true }, ...flowers];

  const petal = petalGeo(0.3, 0.13, "#D46F98", "#FCE3EE");
  const opening = [[-1.2, -1.3, 0], [1.6, -1.9, 2.5], [-1.9, 0.9, 5], [2.2, 1.0, 7.5]].map(([x, z, delay]) => {
    const g = new THREE.Group();
    g.position.set(x, 0.03, z);
    const petals = [];
    [[7, 0.25, 0.85], [9, 0.5, 1.0], [11, 0.75, 1.08]].forEach(([n, open, sc], ring) => {
      for (let i = 0; i < n; i++) {
        const piv = new THREE.Group();
        piv.rotation.y = (i / n) * Math.PI * 2 + ring * 0.3;
        const m = new THREE.Mesh(petal, lam);
        m.scale.setScalar(sc);
        piv.add(m);
        g.add(piv);
        petals.push({ m, open: open + ring * 0.12 + 0.25 });
      }
    });
    const pod = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.032, 0.05, 8), new THREE.MeshLambertMaterial({ color: "#F2C14E" }));
    pod.position.y = 0.03;
    g.add(pod);
    scene.add(g);
    return { g, petals, delay, ph: rand(0, 6) };
  });

  const paintTex = new THREE.TextureLoader();
  paintTex.setCrossOrigin("anonymous");
  const tex = paintTex.load(imageSrc(painting.image_url));
  tex.colorSpace = THREE.SRGBColorSpace;
  const ar = painting.aspect_ratio || 1.3;
  const pw = 1.35;
  const ph = pw / ar;
  const wallMats = [];
  const makeWall = (mirror) => {
    const grp = new THREE.Group();
    const side = mirror ? THREE.DoubleSide : THREE.FrontSide;
    const wm = new THREE.ShaderMaterial({ vertexShader: uvVert, fragmentShader: wallFrag, transparent: true, depthWrite: !mirror, side, uniforms: { uFade: { value: 0 }, uMirrored: { value: mirror ? 1 : 0 }, uPlaster: { value: C("#F1E4D2") } } });
    const fm = new THREE.MeshBasicMaterial({ color: "#3B2E2A", transparent: true, opacity: 0, side, depthWrite: !mirror });
    const pm = new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0, side, toneMapped: false, depthWrite: !mirror });
    wallMats.push({ wm, fm, pm, mirror, grp });
    const wall = new THREE.Mesh(new THREE.PlaneGeometry(9, 9), wm);
    wall.position.set(0, 4.5, WALL_Z);
    const frame = new THREE.Mesh(new THREE.PlaneGeometry(pw + 0.08, ph + 0.08), fm);
    frame.position.set(0, PAINT_Y, WALL_Z + 0.01);
    const pic = new THREE.Mesh(new THREE.PlaneGeometry(pw, ph), pm);
    pic.position.set(0, PAINT_Y, WALL_Z + 0.02);
    grp.add(wall, frame, pic);
    if (mirror) grp.scale.y = -1;
    else [wall, frame, pic].forEach((m, i) => (m.renderOrder = 4 + i));
    scene.add(grp);
  };
  makeWall(true);
  makeWall(false);

  const state = { stage: "pond", t0: performance.now(), st: performance.now(), yaw: 0.35, pitch: -0.12, camZ: 0, calm: 0, mist: 1, mirror: 0, wall: 0, real: 0, ri: 0, fps: 60, low: 0, ripples: 0 };
  const ray = new THREE.Raycaster();
  const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const hit = new THREE.Vector3();

  const ripple = (cx, cy, k = 1) => {
    if (state.stage !== "pond") return null;
    const r = canvas.getBoundingClientRect();
    ray.setFromCamera(new THREE.Vector2(((cx - r.left) / r.width) * 2 - 1, -((cy - r.top) / r.height) * 2 + 1), camera);
    if (!ray.ray.intersectPlane(plane, hit) || hit.distanceTo(camera.position) > 30) return null;
    rip[state.ri++ % rip.length].set(hit.x, hit.z, wu.uTime.value, k);
    state.ripples++;
    all.forEach((g) =>
      g.list.forEach((p) => {
        const d = Math.hypot(p.x - hit.x, p.z - hit.z);
        if (d > 3.2) return;
        const f = (1 - d / 3.2) * k;
        p.bob = Math.min(1.4, p.bob + f);
        p.vx += ((p.x - hit.x) / (d + 0.3)) * f * 0.35;
        p.vz += ((p.z - hit.z) / (d + 0.3)) * f * 0.35;
      }),
    );
    return { x: hit.x, z: hit.z };
  };

  const resize = () => {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.fov = w < h ? 72 : 62;
    camera.updateProjectionMatrix();
  };
  resize();
  window.addEventListener("resize", resize);
  const o = new THREE.Object3D();
  let raf;
  let last = performance.now();
  const approach = (v, to, k) => v + (to - v) * k;
  const frame = (now) => {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    const t = (now - state.t0) / 1000;
    const st = (now - state.st) / 1000;
    if (dt > 0) state.fps = approach(state.fps, 1 / dt, 0.05);
    if (t > 3 && state.stage === "pond") {
      state.low = state.fps < 22 ? state.low + dt : 0;
      if (state.low > 2.5 && onLowFps) {
        onLowFps();
        return;
      }
    }
    wu.uTime.value = t;
    const k = 1 - Math.exp(-dt * 1.2);
    const s = state.stage;
    const calmT = s === "pond" ? 0 : 1;
    state.calm = approach(state.calm, calmT, 1 - Math.exp(-dt * 1.1));
    state.mist = approach(state.mist, s === "pond" ? 1 : 0.2, k);
    state.mirror = approach(state.mirror, s === "pond" || s === "calm" ? 0 : 1, 1 - Math.exp(-dt * 1.4));
    state.real = approach(state.real, s === "lift" || s === "hold" ? 1 : 0, 1 - Math.exp(-dt * 2.2));
    wu.uCalm.value = state.calm;
    wu.uMirror.value = state.mirror;
    mists.forEach((m, i) => {
      m.material.uniforms.uMist.value = state.mist;
      m.position.y = 0.25 + 0.45 * state.mist + Math.sin(t * 0.2 + i) * 0.1;
    });
    wallMats.forEach(({ wm, fm, pm, mirror, grp }) => {
      const f = mirror ? state.mirror * 0.85 : state.real;
      grp.visible = f > 0.01;
      wm.uniforms.uFade.value = f;
      fm.opacity = f;
      pm.opacity = f;
    });
    if (s === "pond") state.yaw += dt * 0.025;
    else {
      const ty = 0;
      const d = Math.atan2(Math.sin(ty - state.yaw), Math.cos(ty - state.yaw));
      state.yaw += d * (1 - Math.exp(-dt * 1.2));
      const lift = s === "hold" ? 1 : s === "lift" ? smooth(Math.min(1, st / 4.5)) : 0;
      const tp = s === "calm" || s === "reflect" ? -0.6 : -0.6 + lift * 0.61;
      state.pitch = approach(state.pitch, tp, s === "lift" || s === "hold" ? 1 : 1 - Math.exp(-dt * 1.0));
      state.camZ = approach(state.camZ, s === "lift" || s === "hold" ? -1.0 * lift : 0, 1);
    }
    camera.position.set(0, 1.6 + Math.sin(t * 0.5) * 0.01 * (1 - state.calm), state.camZ);
    camera.rotation.set(state.pitch, state.yaw, 0, "YXZ");
    wu.uCam.value.copy(camera.position);
    const still = 1 - state.calm;
    all.forEach((g) => {
      g.list.forEach((p, i) => {
        p.vx *= Math.exp(-dt * 0.8);
        p.vz *= Math.exp(-dt * 0.8);
        p.x += (p.vx + (p.bx - p.x) * 0.05) * dt * still + Math.sin(t * 0.1 + p.ph) * 0.0015 * still;
        p.z += (p.vz + (p.bz - p.z) * 0.05) * dt * still;
        p.bob *= Math.exp(-dt * 0.9);
        const y = g.y + (Math.sin(t * 1.3 + p.ph) * 0.012 + Math.sin(t * 5 + p.ph) * 0.04 * p.bob) * still;
        o.position.set(p.x, y, p.z);
        o.rotation.set(Math.sin(t * 4 + p.ph) * 0.12 * p.bob * still, p.rot + t * 0.01 * still, 0);
        o.scale.setScalar(p.s);
        o.updateMatrix();
        g.mesh.setMatrixAt(i, o.matrix);
      });
      g.mesh.instanceMatrix.needsUpdate = true;
    });
    opening.forEach((b) => {
      const op = smooth(Math.min(1, Math.max(0, (t - b.delay) / 9)));
      b.petals.forEach((p) => (p.m.rotation.x = -(0.08 + p.open * op * 1.1)));
      b.g.position.y = 0.03 + Math.sin(t * 1.2 + b.ph) * 0.01 * still;
    });
    renderer.render(scene, camera);
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  let drag = null;
  let lastRip = 0;
  const down = (e) => {
    drag = { x: e.clientX, y: e.clientY };
    return ripple(e.clientX, e.clientY, 1);
  };
  const move = (e) => {
    if (!drag) return null;
    if (state.stage === "pond") {
      state.yaw += (e.clientX - drag.x) * 0.0035;
      state.pitch = Math.max(-0.9, Math.min(0.35, state.pitch + (e.clientY - drag.y) * 0.0025));
    }
    drag = { x: e.clientX, y: e.clientY };
    const now = performance.now();
    if (now - lastRip < 140) return null;
    lastRip = now;
    return ripple(e.clientX, e.clientY, 0.6);
  };
  const up = () => (drag = null);
  window.__afaeLotus = { info: () => ({ stage: state.stage, fps: Math.round(state.fps), ripples: state.ripples, calm: +state.calm.toFixed(2), mirror: +state.mirror.toFixed(2), real: +state.real.toFixed(2), pitch: +state.pitch.toFixed(2), mode: "3d" }) };

  return {
    down,
    move,
    up,
    setStage: (s) => {
      state.stage = s;
      state.st = performance.now();
    },
    destroy: () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      renderer.dispose();
      tex.dispose();
    },
  };
}
